import Vue from 'vue';
import Router from 'vue-router';


import BrowseLocation from '@/components/BrowseLocation';
import BrowseSkills from '@/components/BrowseSkills';
import BrowseInterests from '@/components/BrowseInterests';

import EffortLayout from '@/layouts/EffortLayout';
import TaskBrowse from '@/components/TaskBrowse';
import TasksFollowing from '@/components/TasksFollowing';
import TasksRegistered from '@/components/TasksRegistered';

import EffortDetailsLayout from '@/layouts/EffortDetailsLayout';
import TaskDetails from '@/components/TaskDetails';
import TaskDetailsChat from '@/components/TaskDetailsChat';
import TaskDetailsPeople from '@/components/TaskDetailsPeople';

import OrganizeLayout from '@/layouts/OrganizeLayout';
import OrganizerProjects from '@/components/OrganizerProjects';
import OrganizerTasks from '@/components/OrganizerTasks';
import OrganizerCompletions from '@/components/OrganizerCompletions';
import TaskCompletionDetails from '@/components/TaskCompletionDetails';


import Login from '@/components/Login';
import EmailLogin from '@/components/EmailLogin';
import Logout from '@/components/Logout';
import Splash from '@/components/Splash';

import TasksNew from '@/components/TasksNew';
import TaskEdit from '@/components/TaskEdit';
import TaskAssigned from '@/components/TaskAssigned';
import DirectCompletionQuestions from '@/components/DirectCompletionQuestions';

import Projects from '@/components/Projects';
import ProjectEdit from '@/components/ProjectEdit';
import ProjectDetails from '@/components/ProjectDetails';

import Messages from '@/components/Messages';
import MessagePrivateChat from '@/components/MessagePrivateChat';
import MessagesFilter from '@/components/MessagesFilter';


import UserLayout from '@/layouts/UserLayout';
import UserProfile from '@/components/UserProfile';
import UserProfileEdit from '@/components/UserProfileEdit';
import UserVault from '@/components/UserVault';

import store from '@/store';

import { getNewTaskId } from '@/api';
import get from 'lodash/get';

Vue.use(Router);

// handles /effort/new route and fetches a new task id
// and redirects user to the task edit form
async function newEffortGuard(to, from, next) {
  const userToken = store.getters['auth/userToken'];
  const { data } = await getNewTaskId(userToken);
  const taskId = get(data, '[1].data.details.task_id');
  next({ name: 'TaskEdit', params: { id: taskId } });
}


const router = new Router({
  routes: [
    {
      path: '/',
      name: 'Splash',
      component: Splash,
      meta: { requiresAuth: false },
    },
    {
      path: '/effort/direct/:ref',
      name: 'DirectCompletionQuestions',
      component: DirectCompletionQuestions,
      meta: { requiresAuth: false },
    },
    {
      path: '/location',
      alias: '/browse',
      name: 'BrowseLocation',
      component: BrowseLocation,
      meta: { requiresAuth: true, hideBottombar: true, activity: 'tasks' },
    },
    {
      path: '/skills',
      name: 'BrowseSkills',
      component: BrowseSkills,
      meta: { requiresAuth: true, hideBottombar: true, activity: 'tasks' },
    },
    {
      path: '/interests',
      name: 'BrowseInterests',
      component: BrowseInterests,
      meta: { requiresAuth: true, hideBottombar: true, activity: 'tasks' },
    },
    {
      path: '/messages/filter',
      name: 'MessageFilter',
      component: MessagesFilter,
      meta: { requiresAuth: true, activity: 'messages' },
    },
    {
      path: '/messages',
      name: 'Messages',
      component: Messages,
      meta: { requiresAuth: true, activity: 'messages', query: { activeTabbar: 'private' } },
    },
    {
      path: '/messages/mentions',
      name: 'MessageMentions',
      component: Messages,
      meta: { requiresAuth: true, activity: 'messages', query: { activeTabbar: 'mentions' } },
    },
    {
      path: '/messages/squads',
      name: 'MessageSquads',
      component: Messages,
      meta: { requiresAuth: true, activity: 'messages', query: { activeTabbar: 'squads' } },
    },
    {
      path: '/messages/:id/chat',
      name: 'MessagePrivateChat',
      component: MessagePrivateChat,
      meta: { requiresAuth: true, activity: 'messages', hideBottombar: true },
    },
    {
      path: '/organizer',
      component: OrganizeLayout,
      children: [
        {
          path: 'projects',
          name: 'OrganizerProjects',
          component: OrganizerProjects,
          meta: { requiresAuth: true, activity: 'organize' },
        },
        {
          path: 'effort',
          name: 'OrganizerTasks',
          component: OrganizerTasks,
          meta: { requiresAuth: true, activity: 'organize' },
        },
        {
          path: 'completions',
          name: 'OrganizerCompletions',
          component: OrganizerCompletions,
          meta: { requiresAuth: true, activity: 'organize' },
        },
      ],
    },
    {
      path: '/organizer/completions/:id',
      name: 'TaskCompletionDetails',
      component: TaskCompletionDetails,
      meta: { requiresAuth: true, activity: 'organize' },
    },
    {
      path: '/effort',
      component: EffortLayout,
      children: [
        {
          path: '/',
          name: 'Tasks',
          component: TaskBrowse,
          meta: { requiresAuth: true, activity: 'tasks', query: { active: 'browse' } },
        },
        {
          path: 'following',
          name: 'TasksFollowing',
          component: TasksFollowing,
          meta: { requiresAuth: true, activity: 'tasks', query: { active: 'following' } },
        },
        {
          path: 'registered',
          name: 'TasksRegistered',
          component: TasksRegistered,
          meta: { requiresAuth: true, activity: 'tasks', query: { active: 'registered' } },
        },
      ],
    },
    {
      path: '/effort/new',
      name: 'TasksNew',
      component: TasksNew,
      meta: { requiresAuth: true, activity: 'organize' },
      beforeEnter: newEffortGuard,
    },
    {
      path: '/effort/:id',
      component: EffortDetailsLayout,
      children: [
        {
          path: 'people',
          name: 'TaskDetailsPeople',
          component: TaskDetailsPeople,
          meta: {
            requiresAuth: true, activity: 'tasks', query: { activeTabbar: 'people' },
          },
        },
        {
          path: 'chat',
          name: 'TaskDetailsChat',
          component: TaskDetailsChat,
          meta: {
            requiresAuth: true, activity: 'tasks', query: { activeTabbar: 'messages' }, hideBottombar: true,
          },
        },
        {
          path: '/',
          name: 'TaskDetails',
          component: TaskDetails,
          meta: { requiresAuth: true, activity: 'tasks', query: { activeTabbar: 'overview' } },
        },
      ],
    },
    {
      path: '/effort/:id/edit',
      name: 'TaskEdit',
      component: TaskEdit,
      meta: { requiresAuth: true, activity: 'organize' },
    },
    {
      // my tasks view
      path: '/users/:id/effort',
      name: 'TaskAssigned',
      component: TaskAssigned,
      meta: { requiresAuth: true },
    },
    {
      path: '/login/:token',
      name: 'EmailLogin',
      component: EmailLogin,
      meta: { requiresAuth: false },
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: { requiresAuth: false },
    },
    {
      path: '/projects',
      name: 'Projects',
      component: Projects,
      meta: { requiresAuth: true },
    },
    {
      // project details view
      path: '/projects/:id/',
      name: 'ProjectOverview',
      component: ProjectDetails,
      meta: { requiresAuth: true, activity: 'organize', query: { activeTabbar: 'overview' } },
    },
    {
      path: '/projects/:id/effort',
      name: 'ProjectTasks',
      component: ProjectDetails,
      meta: { requiresAuth: true, activity: 'organize', query: { activeTabbar: 'tasks' } },
    },
    {
      path: '/projects/:id/chat',
      name: 'ProjectChat',
      component: ProjectDetails,
      meta: { requiresAuth: true, activity: 'organize', query: { activeTabbar: 'chat' }, hideBottombar: true },
    },
    {
      path: '/projects/:id/edit',
      name: 'ProjectEdit',
      component: ProjectEdit,
      meta: { requiresAuth: true, activity: 'organize' },
    },
    {
      path: '/user/:id',
      component: UserLayout,
      children: [
        {
          path: 'profile',
          name: 'UserProfile',
          component: UserProfile,
          meta: {
            requiresAuth: true, activity: 'profile',
          },
        },
        {
          path: 'vault',
          name: 'UserVault',
          component: UserVault,
          meta: { requiresAuth: true, activity: 'profile' },
        },
      ],
    },
    {
      path: '/profile',
      redirect: () => {
        const userId = store.getters['auth/userId'];
        return { name: 'UserProfile', params: { id: userId } };
      },
    },
    {
      path: '/vault',
      redirect: () => {
        const userId = store.getters['auth/userId'];
        return { name: 'UserVault', params: { id: userId } };
      },
    },
    {
      path: '/profile/edit',
      name: 'UserProfileEdit',
      component: UserProfileEdit,
      meta: { requiresAuth: true, activity: 'profile' },
    },
    {
      path: '/logout',
      name: 'Logout',
      component: Logout,
    },
  ],
});


// add authentication guard to the routes

router.beforeEach((to, from, next) => {
  // const validatingToken = store.getters['auth/validatingToken'];
  const authorized = store.getters['auth/userToken'] || false;
  // when validating token is true, redirect show splash
  if (to.meta.requiresAuth && !authorized) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
  } else {
    next();
  }
});


export default router;



// WEBPACK FOOTER //
// ./src/router/index.js